/**
 * Extract original order number from additionalDescription
 * Pattern: "Rücknahme aus AU-2138277" or "Rücknahme aus BA-1234567"
 * @param {string} additionalDescription
 * @returns {string|null} - The original order number (e.g., "AU-2138277") or null
 */
const extractOriginalOrderNumber = (additionalDescription) => {
  if (!additionalDescription) return null

  // Look for patterns like "Rücknahme aus AU-2138277" or "Rücknahme aus BA-1234567"
  const match = additionalDescription.match(/Rücknahme aus ([A-ZÜ]+-\d+)/)
  return match ? match[1] : null
}

/**
 * Calculate returned pallet amounts per order from return orders
 * @param {Array<{items: Array<{sku: string, amount: number, additionalDescription?: string}>, orderType: string, orderNumber2: string}>} returnOrders - Array of return orders with items containing description2
 * @returns {Map<string, {amount: number, returnOrderIds: string[]}>} - Map of "orderType-orderNumberShort" (e.g., "AU-2138277") to returned pallet data
 */
export const calculateReturnedPalletsPerOrder = (returnOrders) => {
  if (!returnOrders || returnOrders.length === 0) {
    return new Map()
  }

  const returnedAmountsMap = new Map()

  // Process each return order
  for (const returnOrder of returnOrders) {
    if (!returnOrder.items || returnOrder.items.length === 0) continue

    // Create return order ID in format "orderType-orderNumber2" (e.g., "RÜ-2082007")
    const returnOrderId =
      returnOrder.orderType && returnOrder.orderNumber2 ? `${returnOrder.orderType}-${returnOrder.orderNumber2}` : null

    // First, find the original order number from any item's additionalDescription
    let originalOrderNumber = null
    for (const item of returnOrder.items) {
      if (item?.additionalDescription) {
        const extracted = extractOriginalOrderNumber(item.additionalDescription)
        if (extracted) {
          originalOrderNumber = extracted
          break // Found it, no need to continue
        }
      }
    }

    // If we found an original order number, find the pallet amount (sku "0040210")
    if (originalOrderNumber) {
      let palletAmount = 0
      for (const item of returnOrder.items) {
        if (item?.sku === "0040210" && item?.amount) {
          palletAmount += item.amount
        }
      }

      // Only add if we found pallet items
      if (palletAmount > 0) {
        const existing = returnedAmountsMap.get(originalOrderNumber) || { amount: 0, returnOrderIds: [] }
        existing.amount += palletAmount
        if (returnOrderId) {
          existing.returnOrderIds.push(returnOrderId)
        }
        returnedAmountsMap.set(originalOrderNumber, existing)
      }
    }
  }

  return returnedAmountsMap
}
